import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Pagination,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useFormik } from 'formik'
import * as yup from 'yup'
import axios from 'src/utilities/http-client'
import { AxiosError } from 'axios'
import { Course } from 'src/v3/core/types'
import { processImageFile, uploadImageToS3 } from 'src/v3/utilities/s3'
import {
  AddIcon,
  AddImageIcon,
  DeleteIcon,
  EditIcon,
  PreviewIcon,
  SubmitIcon,
} from 'src/v3/core/icons'
import ConfirmAction from 'src/v3/components/confirm-action'
import Loading from 'src/v3/components/loading'
import EntityList from 'src/components/common/entity-list'
import CourseCard from 'src/components/common/course-card'
import ModernLinkedServiceSelector from './template/components/modern-linked-service-selector'
import {
  selectActiveLinkedService,
  fetchLearningPaths,
  selectLearningPathsByLinkedService,
  selectLearningPathsStatus,
  fetchLearningPathCourses,
  selectLearningPathCourses,
  selectLearningPathCoursesStatus,
  selectSections,
  selectSectionsStatus,
  fetchSections,
  fetchLinkedServices,
  selectLinkedServicesStatus,
} from './create.slice'

type UnpublishedCoursesResponse = {
  message: string
  unpublishedCourses: { id: string; name: string }[]
}

// The learning path save endpoints reject with a 400 listing newly added
// FOAMfrat courses that aren't published. Returns a message naming them, or null for
// any other error.
let getUnpublishedCoursesMessage = (error: unknown): string | null => {
  if (!(error instanceof AxiosError) || error.response?.status !== 400)
    return null
  let data = error.response.data as UnpublishedCoursesResponse | undefined
  if (!data?.unpublishedCourses?.length) return null
  let names = data.unpublishedCourses.map((c) => c.name).join(', ')
  return `These FOAMfrat courses are unpublished and can't be added: ${names}. Remove them and save again.`
}

// Only FOAMfrat courses are blocked; linked services publish their own courses.
let isUnpublishedFoamfratCourse = (c: Course) =>
  !c.linkedServiceID && c.published === 'unpublished'

let UnpublishedChip = ({ entity }: { entity: Course }) =>
  isUnpublishedFoamfratCourse(entity) ? (
    <Chip label="Unpublished" color="warning" size="small" />
  ) : null

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let learningPaths = useAppSelector(
    selectLearningPathsByLinkedService(activeLinkedService),
  )
  let learningPathsStatus = useAppSelector(selectLearningPathsStatus)
  let courses = useAppSelector(selectLearningPathCourses)
  let coursesStatus = useAppSelector(selectLearningPathCoursesStatus)
  let sections = useAppSelector(selectSections)
  let sectionsStatus = useAppSelector(selectSectionsStatus)
  let [saving, setSaving] = useState<boolean>(false)
  let [selected, setSelected] = useState<string>('')
  let [action, setAction] = useState<'create' | 'view' | 'edit' | 'delete'>(
    'view',
  )
  let [checkedCourses, setCheckedCourses] = useState<string[]>([])
  let [page, setPage] = useState<number>(1)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let [unpublishedOnSave, setUnpublishedOnSave] = useState<Course[]>([])

  let selectedPath = learningPaths.find((lp) => lp.id === selected)

  // "Remove and save" would leave nothing, which the API rejects.
  let allCheckedUnpublished =
    unpublishedOnSave.length > 0 &&
    checkedCourses.every((id) => unpublishedOnSave.some((c) => c.id === id))

  let saveLearningPath = (
    values: { id: string; name: string; category: string },
    courseIDs: string[],
    resetForm: () => void,
  ) => {
    setSaving(true)
    let body = {
      name: values.name,
      category: values.category,
      courses: courseIDs,
      linkedServiceID:
        activeLinkedService === 'unselected' ? undefined : activeLinkedService,
    }
    let request = !values.id
      ? axios.post('/api/v2/create/learning-path', body)
      : axios.put('/api/v2/create/learning-path', { id: values.id, ...body })
    request
      .then(() => dispatch(fetchLearningPaths()))
      .then(() => setAction('view'))
      .then(() => setSaving(false))
      .then(() => resetForm())
      .catch((err) => {
        let unpublished = getUnpublishedCoursesMessage(err)
        if (unpublished) {
          setErrorMessage(unpublished)
          setSaving(false)
          return
        }
        setErrorMessage(
          err?.message ||
            (!values.id
              ? 'Error Creating Learning Path'
              : 'Error Updating Learning Path'),
        )
        dispatch(fetchLearningPaths())
        setAction('view')
        setSaving(false)
      })
  }

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      category: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      name: '',
      category: '',
    },
    onSubmit: (values, { resetForm }) => {
      // The API rejects an empty path: it would drop out of every learning path list.
      if (!checkedCourses.length) {
        setErrorMessage('Select at least one course.')
        return
      }
      // Learners can't take unpublished FOAMfrat courses, so a path that keeps one can't be
      // completed. Ask before saving rather than silently keeping or dropping it.
      let unpublished = courses.filter(
        (c) => checkedCourses.includes(c.id) && isUnpublishedFoamfratCourse(c),
      )
      if (unpublished.length) {
        setUnpublishedOnSave(unpublished)
        return
      }
      saveLearningPath(values, checkedCourses, resetForm)
    },
  })

  let onSelection = (status: typeof action, id: string) => {
    let selectedLearningPath = learningPaths.find((lp) => lp.id === id)
    form.setFieldValue('id', selectedLearningPath?.id || '')
    form.setFieldValue('name', selectedLearningPath?.name || '')
    form.setFieldValue('category', selectedLearningPath?.category || '')
    setCheckedCourses(selectedLearningPath?.courseIDs || [])
    setSelected(id)
    setAction(status)
  }

  let onImageUpload = (id: string, t: any) => {
    setSaving(true)
    return axios
      .put('/api/v2/create/learning-path/image', { id })
      .then((response) => uploadImageToS3(t, response.data))
      .then(() => dispatch(fetchLearningPaths()))
      .then(() => setSaving(false))
      .catch((err) => {
        setSaving(false)
        setErrorMessage(err?.message || 'Error Uploading Image')
      })
  }

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  useEffect(() => {
    if (learningPathsStatus === 'uninitialized') {
      dispatch(fetchLearningPaths())
    }
  }, [learningPathsStatus])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchLearningPathCourses())
    }
  }, [coursesStatus])

  useEffect(() => {
    if (sectionsStatus === 'uninitialized') {
      dispatch(fetchSections())
    }
  }, [sectionsStatus])

  useEffect(() => {
    setSelected('')
    setPage(1)
  }, [activeLinkedService])

  useEffect(() => {
    if (learningPaths.length <= (page - 1) * 10) {
      setPage(1)
    }
  }, [learningPaths.length])

  return (
    <Container maxWidth="xl">
      <Typography variant="h1">Learning Paths</Typography>
      <Box sx={{ mb: 2, maxWidth: 300 }}>
        <ModernLinkedServiceSelector />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 2, columnGap: 3 }}>
        <Box sx={{ flex: '1 0 320px' }}>
          <Box sx={{ mb: 1.5 }}>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => onSelection('create', '')}
            >
              Create Learning Path
            </Button>
          </Box>
          {learningPaths.slice(10 * (page - 1), 10 * page).map((sc) => (
            <Paper
              key={sc.id}
              sx={{
                px: 2,
                py: 1.5,
                mb: 1,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              onClick={() => onSelection('view', sc.id)}
            >
              <Typography>{sc.name}</Typography>
              <Typography color="text.secondary">
                {sc.courseIDs?.length || 'No'} Courses
              </Typography>
            </Paper>
          ))}
          <Box sx={{ mt: -1 }}>
            {learningPaths.length ? (
              <Pagination
                size="small"
                sx={{ py: '5px', display: 'flex', justifyContent: 'center' }}
                count={Math.ceil(learningPaths.length / 10)}
                page={page}
                onChange={(e: React.ChangeEvent<unknown>, value: number) => {
                  setPage(value)
                }}
                showFirstButton
                showLastButton
              />
            ) : (
              <Typography>There are no learning paths to display.</Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ flex: '1 0 320px' }}>
          <form onSubmit={form.handleSubmit}>
            {selected || action === 'create' ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {selected && (
                  <Avatar
                    variant="rounded"
                    alt={selectedPath?.name}
                    src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${selectedPath?.imageName}`}
                    sx={{ height: 200, width: 200, mb: -2 }}
                  />
                )}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      textAlign: 'center',
                    }}
                  >
                    {action === 'create'
                      ? 'Create Learning Path'
                      : selectedPath?.name}
                  </Typography>
                  {action !== 'create' && (
                    <Tooltip title="Upload Thumbnail" placement="right">
                      <IconButton color="info" component="label">
                        <AddImageIcon />
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          hidden
                          onChange={(event) =>
                            processImageFile(event, {
                              onLoadingStart: () => {},
                              onLoadingEnd: (t: any) => {
                                onImageUpload(selectedPath?.id || '', t)
                              },
                            })
                          }
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                {action !== 'create' && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 1,
                      mt: -1,
                    }}
                  >
                    {action === 'view' || action === 'delete' ? (
                      <Button
                        sx={{ flex: '0 0 120px' }}
                        variant="outlined"
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={() =>
                          onSelection('edit', selectedPath?.id || '')
                        }
                      >
                        Edit
                      </Button>
                    ) : (
                      <Button
                        sx={{ flex: '0 0 120px' }}
                        variant="outlined"
                        color="primary"
                        startIcon={<PreviewIcon />}
                        onClick={() =>
                          onSelection('view', selectedPath?.id || '')
                        }
                      >
                        View
                      </Button>
                    )}
                    <Button
                      sx={{ flex: '0 0 120px' }}
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        setAction('delete')
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                )}
                {(action === 'create' || action === 'edit') && (
                  <>
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Name"
                      name="name"
                      type="text"
                      fullWidth
                      value={form.values.name}
                      onChange={form.handleChange}
                      error={form.touched.name && !!form.errors.name}
                    />
                    <TextField
                      variant="outlined"
                      size="small"
                      label="Category"
                      name="category"
                      type="text"
                      fullWidth
                      value={form.values.category}
                      onChange={form.handleChange}
                      error={form.touched.category && !!form.errors.category}
                    />
                  </>
                )}
                <Box sx={{ mt: -1, mb: -3, width: '100%' }}>
                  <EntityList
                    entities={(action === 'create' || action === 'edit'
                      ? courses.filter(
                          (c) =>
                            (!c.linkedServiceID ||
                              c.linkedServiceID === activeLinkedService) &&
                            // Unpublished FOAMfrat courses can't be taken, so
                            // they can't be newly added; ones already on the
                            // path stay visible (flagged) so they can be removed.
                            (!isUnpublishedFoamfratCourse(c) ||
                              (action === 'edit' &&
                                selectedPath?.courseIDs.includes(c.id))),
                        )
                      : courses.filter((c) =>
                          selectedPath?.courseIDs.includes(c.id),
                        )
                    ).map((c) => ({
                      ...c,
                      section: sections?.find((s) => s.id === c.sectionID)
                        ?.name,
                    }))}
                    checkbox={
                      action === 'create' || action === 'edit'
                        ? {
                            checked: checkedCourses,
                            onClick: setCheckedCourses,
                          }
                        : undefined
                    }
                    customAction={UnpublishedChip}
                    info={{ card: CourseCard }}
                  />
                </Box>
                {(action === 'create' || action === 'edit') && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Button
                      sx={{ flex: '0 0 120px' }}
                      variant="contained"
                      color="primary"
                      size="large"
                      type="submit"
                      startIcon={<SubmitIcon />}
                    >
                      {action === 'create' ? 'Create' : 'Update'}
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography
                sx={{
                  textAlign: 'center',
                }}
              >
                Select a learning path to view details or create a new one.
              </Typography>
            )}
          </form>
        </Box>
      </Box>
      <ConfirmAction
        executeAction={() =>
          axios
            .delete(
              `/api/v2/create/learning-path?learningPathID=${selectedPath?.id}`,
            )
            .then(() => setSelected(''))
            .then(() => dispatch(fetchLearningPaths()))
        }
        open={action}
        setOpen={(value: typeof action) => {
          setAction(!value ? 'view' : value)
        }}
        openText="delete"
        title="Confirm Deletion"
        text={`Are you sure you want to delete ${selectedPath?.name}?`}
        onFailure={(err) =>
          setErrorMessage(err.message || 'Error Deleting Learning Path')
        }
      />
      <Dialog
        open={unpublishedOnSave.length > 0}
        onClose={() => setUnpublishedOnSave([])}
        aria-labelledby="unpublished-courses-dialog-title"
      >
        <DialogTitle id="unpublished-courses-dialog-title">
          This learning path includes unpublished FOAMfrat courses
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Learners can&apos;t take these unpublished FOAMfrat courses, so they
            won&apos;t be able to complete this learning path:
          </DialogContentText>
          <ul className="mt-2 list-disc pl-6">
            {unpublishedOnSave.map((c) => (
              <li key={c.id}>
                <Typography variant="body2">{c.name}</Typography>
              </li>
            ))}
          </ul>
          {allCheckedUnpublished && (
            <DialogContentText className="mt-2">
              Every course on this path is an unpublished FOAMfrat course. Add a
              published course before removing them.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnpublishedOnSave([])}>Cancel</Button>
          <Button
            onClick={() => {
              setUnpublishedOnSave([])
              saveLearningPath(form.values, checkedCourses, form.resetForm)
            }}
          >
            Save anyway
          </Button>
          <Button
            variant="contained"
            autoFocus={!allCheckedUnpublished}
            disabled={allCheckedUnpublished}
            onClick={() => {
              let remaining = checkedCourses.filter(
                (id) => !unpublishedOnSave.some((c) => c.id === id),
              )
              setUnpublishedOnSave([])
              setCheckedCourses(remaining)
              saveLearningPath(form.values, remaining, form.resetForm)
            }}
          >
            Remove and save
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          <strong>Error with request: </strong>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading
        entities={[coursesStatus, learningPathsStatus, sectionsStatus]}
        loading={saving}
      />
    </Container>
  )
}
